import React, { useContext, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { TextVariant } from '@naf/text';
import { List } from '@styled-icons/material/List';
import { Message } from '@naf/input';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { Map } from '@styled-icons/material-outlined/Map';
import { breakpoints } from '@naf/theme';
import { LocationSlugs } from '@appTypes/nafLocations';
import ArrowHoverLink from '../../components/ArrowHoverLink';
import { Spinner } from '../../../../components/spinner/Spinner';
import { useGetNafLocations } from '../../../../hooks/useGetNafLocations';
import { InternalSearchCardType } from '../../../../components/layout/InternalSearchMetaData';
import LocationChips from '../../components/LocationChips';
import { LayoutWithMainContent } from '../../../../components/layout/Layout';
import { NafLocationsAppContext } from '../../NafLocationsApp';
import { useWindowLocation } from '../../../../hooks/Window/useWindowLocation';
import { CenterType, NafCenterLinkData } from '../../../../../../types/centerType';
import CountText from '../../components/CountText';
import { formatDistance } from './NafCenters.utils';
import { useLocationSort } from './useLocationSort';
import LocationSortButton from './LocationSortButton';
import CountyFilter from './CountyFilter';
import * as S from './NafCenters.styles';

const GoogleMaps = loadable(() => import('../../components/GoogleMap'));

const createNafCenterUrl = (data: NafCenterLinkData) => `/bilverksted-og-tester/naf-senter/${data.slug}`;

const NafCenters = () => {
  const data = useContext(NafLocationsAppContext);
  const { ingress, seoConfig } = data || {};
  const url = useWindowLocation();
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const params = qs.parse(routerLocation.search, { ignoreQueryPrefix: true });
  const showMap = params.showMap === 'true';
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.s})`);

  const { filteredLocations, nafLocationsByCounty, fetchState, error, isFiltering, totalCountInSelectedCounties } =
    useGetNafLocations(LocationSlugs.nafCenters, selectedCounties, 1000);

  const {
    sortedByLocation,
    sortedCenters,
    isFindingLocation,
    locationError,
    handleSortByLocation,
    dismissError,
    reset,
  } = useLocationSort({
    onSuccess: () => {
      setSelectedCounties([]);
      if (isMobile) setIsFilterOpen(false);
    },
  });

  const displayLocations = sortedByLocation ? sortedCenters : filteredLocations;

  const handleCountyChange = (counties: string[]) => {
    setSelectedCounties(counties);
    if (sortedByLocation) reset();
  };

  const markers = useMemo(
    () =>
      showMap
        ? nafLocationsByCounty.flatMap(({ nafLocations }) =>
            nafLocations
              .filter((l) => l.coordinates !== null)
              .map(({ title, ingress: locationIngress, link, coordinates }) => ({
                lat: coordinates.latitude,
                lng: coordinates.longitude,
                title,
                ingress: locationIngress,
                link: createNafCenterUrl(link.data),
              })),
          )
        : null,
    [nafLocationsByCounty, showMap],
  );

  const locationSortButton = (
    <LocationSortButton
      onClick={handleSortByLocation}
      isLoading={isFindingLocation}
      error={locationError}
      onDismissError={dismissError}
    />
  );

  const renderContent = () => {
    if (fetchState === 'initial' || (fetchState === 'isUpdating' && nafLocationsByCounty.length === 0)) {
      return <Spinner />;
    }

    if (error) {
      return <Message error>{error.message}</Message>;
    }

    if (showMap && markers && markers.filter(Boolean).length > 0) {
      return (
        <S.MapWrapper>
          <S.ButtonWrapper>
            <List size={18} />
            <S.MapButton onClick={() => navigate(`?showMap=${!showMap}`)} text="Vis som liste" />
          </S.ButtonWrapper>
          <GoogleMaps markers={markers} />
        </S.MapWrapper>
      );
    }

    return (
      <S.Wrapper>
        <S.ButtonWrapper>
          <Map size={18} />
          <S.MapButton onClick={() => navigate(`?showMap=${!showMap}`)} text="Vis som kart" />
        </S.ButtonWrapper>
        {!sortedByLocation && (
          <CountText
            count={totalCountInSelectedCounties}
            selectedCounties={selectedCounties}
            name="NAF Senter"
            countyNames={Object.fromEntries(nafLocationsByCounty.map(({ county }) => [county.slug, county.name]))}
            pluralSuffix=""
          />
        )}
        <CountyFilter
          nafLocationsByCounty={nafLocationsByCounty}
          selectedCounties={selectedCounties}
          onChange={handleCountyChange}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen((prev) => !prev)}
          topSlot={isMobile ? locationSortButton : undefined}
        />
        <S.HitList>
          {!isMobile && <S.LocationButtonWrapper>{locationSortButton}</S.LocationButtonWrapper>}
          {fetchState === 'isUpdating' && isFiltering ? (
            <Spinner />
          ) : (
            <>
              {displayLocations.map(({ county, nafLocations }) => (
                <div key={county.name}>
                  <S.ResultHeader variant={TextVariant.BodyTextHeader}>{county.name}</S.ResultHeader>
                  {nafLocations.map((location: CenterType) => (
                    <S.BorderWrapper key={location.link.data.slug}>
                      <ArrowHoverLink
                        variant={TextVariant.Header2}
                        to={createNafCenterUrl(location.link.data)}
                        text={`${location.title}${sortedByLocation ? formatDistance(location.distanceInMeters) : ''}`}
                      />
                    </S.BorderWrapper>
                  ))}
                </div>
              ))}
            </>
          )}
        </S.HitList>
      </S.Wrapper>
    );
  };

  return (
    <LayoutWithMainContent
      title="NAF Senter"
      description={ingress || seoConfig?.introduction || 'Liste over NAF Senter i Norge'}
      seoDescription="Her finner du alle NAF-senter i Norge"
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoTitle="NAF Senter"
      internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
      url={url}
    >
      <LocationChips />
      {renderContent()}
    </LayoutWithMainContent>
  );
};

export default NafCenters;
